import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  type LeadCreateRequest,
  leadCreateRequestSchema,
} from "@pbz/schema/lead";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
} from "@pbz/ui-kit";

import { FormLabel } from "@/components/FormLabel";
import { useCreateLead } from "@/queries/lead";
import { useGetCities, useGetCountries } from "@/queries/master";

interface CustomerDetailsProps {
  leadId?: number;
  productId: number;
  onSubmit: (leadId: number) => void;
  defaultValues?: Partial<LeadCreateRequest>;
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  productId,
  onSubmit,
  leadId,
  defaultValues,
}) => {
  const form = useForm<LeadCreateRequest>({
    resolver: zodResolver(leadCreateRequestSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    disabled: !!leadId,
    defaultValues: defaultValues || {
      name: "",
      mobileNumber: "",
      email: "",
      homeCountry: -1,
      city: -1,
    },
  });

  // Get City Options
  const { isLoading: isLoadingCities, data: citiesData } =
    useGetCities(productId);
  const cities = citiesData?.data || [];

  // Get Countries Options
  const { isLoading: isLoadingCountries, data: countriesData } =
    useGetCountries();
  const countries = countriesData?.data || [];

  // Create Lead
  const createLeadMutation = useCreateLead({
    onSuccess: (data) => {
      if (data?.pbMatrixLeadId) {
        toast.success("Lead Created successfully");
        onSubmit?.(data.pbMatrixLeadId);
      }
    },
    onError: (error) => {
      toast.error(
        error.message || "Failed to register user. Please try again.",
      );
      // Keep modal open on error so user can retry
    },
  });

  const handleFormSubmit = (data: LeadCreateRequest) => {
    if (leadId) {
      return;
    }
    createLeadMutation.mutate(data);
  };

  const getSelectedCity = (fieldValue: number) => {
    const selectedCity = cities.find((city) => city.id === fieldValue);
    return selectedCity ? (
      selectedCity.name
    ) : (
      <span className="text-gray-500">Select City</span>
    );
  };

  const getSelectedCountry = (fieldValue: number) => {
    const selectedCountry = countries.find(
      (country) => country.id === fieldValue,
    );
    return selectedCountry ? (
      selectedCountry.countryName
    ) : (
      <span className="text-gray-500">Select Country</span>
    );
  };

  return (
    <Card className="mb-10 bg-white">
      <CardHeader>
        <CardTitle>Customer Details</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobileNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Mobile Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter mobile number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Email ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter email address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="homeCountry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Nationality</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingCountries || !!leadId}
                            variant="outline"
                          >
                            {getSelectedCountry(Number(field.value))}
                            <div className="flex items-center justify-center gap-2">
                              {isLoadingCountries && (
                                <Loader className="h-4 w-4" />
                              )}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => field.onChange(-1)}
                          >
                            <span className="text-gray-500">
                              Select Country
                            </span>
                          </DropdownMenuItem>
                          {countries.map((country) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={country.id}
                              onClick={() => field.onChange(country.id)}
                            >
                              {country.countryName}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>City</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingCities || !!leadId}
                            variant="outline"
                          >
                            {getSelectedCity(Number(field.value))}
                            <div className="flex items-center justify-center gap-2">
                              {isLoadingCities && (
                                <Loader className="h-4 w-4" />
                              )}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => field.onChange(-1)}
                          >
                            <span className="text-gray-500">Select City</span>
                          </DropdownMenuItem>
                          {cities.map((city) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={city.id}
                              onClick={() => field.onChange(city.id)}
                            >
                              {city.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="income"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Salary (AED)</FormLabel>{" "}
                    <FormControl>
                      <Input
                        placeholder="Enter Salary"
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button
                className="bg-blue-500 text-white hover:bg-blue-600"
                disabled={createLeadMutation.isPending || !!leadId}
                type="submit"
              >
                {createLeadMutation.isPending ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CustomerDetails;
